import React from "react";
import { useApp } from "@/context/AppContext";
import { useNavigate } from "react-router-dom";
import api, { errMsg } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { QuotaIndicator } from "@/components/QuotaIndicator";
import { toast } from "sonner";
import { CreditCard, LogOut, Mail, CalendarDays, Sparkles } from "lucide-react";

export default function Account() {
  const { t, user, quota, logout, refreshMe } = useApp();
  const navigate = useNavigate();

  const cancel = async () => {
    if (!window.confirm(t("confirmDelete"))) return;
    try {
      await api.post("/billing/cancel");
      await refreshMe();
      toast.success(t("planUpdated"));
    } catch (err) {
      toast.error(errMsg(err, t("errGeneric")));
    }
  };

  const tierName = { free: t("planFreeName"), pro: t("planProName"), agency: t("planAgencyName") }[user?.subscription_tier] || user?.subscription_tier;
  const created = user?.created_at ? new Date(user.created_at).toLocaleDateString() : "-";

  return (
    <div className="mx-auto max-w-3xl space-y-6">
      <h1 className="font-heading text-2xl font-semibold tracking-tight">{t("accountTitle")}</h1>

      <Card className="p-6">
        <h2 className="font-heading text-lg font-semibold">{t("yourPlan")}</h2>
        <div className="mt-4 flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <span className="flex h-11 w-11 items-center justify-center rounded-lg bg-primary/10 text-primary"><Sparkles className="h-5 w-5" /></span>
            <div>
              <div className="font-heading text-xl font-semibold">{tierName}</div>
              <div className="text-sm capitalize text-muted-foreground">{user?.subscription_status}</div>
            </div>
          </div>
          <Button onClick={() => navigate("/app/pricing")} className="gap-2" data-testid="account-upgrade-button">
            <CreditCard className="h-4 w-4" /> {t("manageSub")}
          </Button>
        </div>
        <div className="mt-6">
          <QuotaIndicator />
        </div>
        {user?.subscription_tier !== "free" && (
          <Button variant="outline" className="mt-6 text-destructive" onClick={cancel} data-testid="account-cancel-button">
            {t("downgrade")}
          </Button>
        )}
      </Card>

      <Card className="p-6">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div className="flex items-center gap-3">
            <Mail className="h-5 w-5 text-muted-foreground" />
            <div>
              <div className="text-xs text-muted-foreground">{t("accountEmail")}</div>
              <div className="text-sm font-medium">{user?.email}</div>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <CalendarDays className="h-5 w-5 text-muted-foreground" />
            <div>
              <div className="text-xs text-muted-foreground">{t("memberSince")}</div>
              <div className="text-sm font-medium">{created}</div>
            </div>
          </div>
        </div>
      </Card>

      <Button variant="outline" className="gap-2" onClick={() => { logout(); navigate("/login"); }} data-testid="account-logout-button">
        <LogOut className="h-4 w-4" /> {t("logout")}
      </Button>
    </div>
  );
}
